/**
 * **债权债务账户区**（`contra` 五类 · 批次 `94` 从资产页抽出）
 *
 * 依据：`AC-Q4.2`（按 `kind` 分组）· `AC-Q4.4`（分组即折叠）· `AC-Q4.5`（卡片四要素）·
 * `AC-Q4.6`（色彩守铁律 8）· 原型 `docs/03-UX与UI设计/15-债权债务_原型.html:288-320`。
 *
 * ## 🔴 本批次改了什么（负责人 2026-10-21 的截图）
 *
 * **修前**：进度 / 到期 / 类型 chip / 结算 / 报销到账 / 还本期 / 明细 / 编辑 / 删除
 * **全挤在一个 `flexWrap` 行**里，只有末尾那组带 `ml:auto` ⇒ 一行放不下时按 DOM 顺序换行，
 * `ml:auto` 只对**第一行**生效 ⇒ 截图里「结算」独占左侧、「编辑 / 删除」掉到第二行最右，
 * 看起来像完全没对齐。
 *
 * **修后**：两行职责分离 ——
 * | 行 | 内容 | 对齐 |
 * |---|---|---|
 * | meta 行 | 进度 · 到期 · 类型 chip（全是 11px 说明文字） | 左对齐、可换行 |
 * | 动作行 | 结算 / 报销到账 / 还本期 / 明细 / 编辑 / 删除 | **右对齐**、固定 `spacing`、统一 `size="small"` |
 *
 * 删除的二次确认**就地替换动作行内容**（不新起一行）—— 否则卡高会跳一下。
 *
 * ## 🔴 为什么独立成组件
 *
 * 资产页与「债权债务」页都要这块，但只有一处该负责"卡片长什么样"。
 * 判据（分组 / 展示符号 / 副行文案 / 进度文案）**全部**在 `features/contra-group.ts`，
 * 本文件只负责排 DOM 与接动作 —— 一旦在这里出现 `if (kind === 'payable')`，就是第二份口径。
 */

import { useState, type ReactElement } from 'react';
import { Box, Button, Card, CardContent, Chip, Divider, Stack, Typography } from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ContraKind, type AccountView } from '@jiaying/shared';
import { listCategories, listLoanProgress } from '../api/endpoints.js';
import { localToday } from '../app/local-day.js';
import { buildContraGroups, type ContraCardModel } from '../features/contra-group.js';
import { settleActionsFor } from '../features/contra-settle.js';
import { Amount } from './Amount.js';
import { Grid } from './Grid.js';
import { ContraTimelineDrawer } from './ContraTimelineDrawer.js';
import { LoanRepayDialog } from './LoanRepayDialog.js';
import { ReimburseDialog } from './ReimburseDialog.js';
import { SettleForm, canBeCounterAccount } from './SettleForm.js';

export interface ContraSectionProps {
  /** 本账本**全部**账户（本组件自己筛 `contra`） */
  accounts: readonly AccountView[];
  /** 新建往来账户（入口按钮） */
  onCreate: () => void;
  /** 打开编辑表单（页面持有 `AccountForm`） */
  onEdit: (account: AccountView) => void;
  /** 删除（页面执行；本组件只管"要不要问第二次"） */
  onDelete: (account: AccountView) => void;
  /** 有保存 / 删除在飞时禁用动作 */
  busy: boolean;
}

export function ContraSection({
  accounts,
  onCreate,
  onEdit,
  onDelete,
  busy,
}: ContraSectionProps): ReactElement {
  const qc = useQueryClient();
  const [collapsedKinds, setCollapsedKinds] = useState<ReadonlySet<ContraKind>>(() => new Set());
  const [settlingId, setSettlingId] = useState<string | null>(null);
  const [timelineId, setTimelineId] = useState<string | null>(null);
  const [reimburseId, setReimburseId] = useState<string | null>(null);
  const [repayId, setRepayId] = useState<string | null>(null);
  const [confirmingId, setConfirmingId] = useState<string | null>(null);

  /** 贷款卡面进度（批量一次拿全；失败不阻断本区 —— 缺了就不显示"还本期"） */
  const loanProgress = useQuery({
    queryKey: ['loanProgress'],
    queryFn: listLoanProgress,
    retry: false,
  });
  /** 分类名表 —— 只给报销弹窗的池标题用 */
  const categories = useQuery({ queryKey: ['categories'], queryFn: listCategories });

  const all = accounts;
  const contraGroups = buildContraGroups(all, loanProgress.data?.items ?? []);
  const contraTotal = all.filter((a) => a.type === 'contra').length;

  const loanProgressOf = (accountId: string): { pendingPeriods: number } | undefined =>
    loanProgress.data?.items.find((x) => x.accountId === accountId);

  const settlingAccount = settlingId === null ? undefined : all.find((a) => a.id === settlingId);
  const reimburseAccount = reimburseId === null ? null : (all.find((a) => a.id === reimburseId) ?? null);
  const repayAccount = repayId === null ? null : (all.find((a) => a.id === repayId) ?? null);
  const counterOptionsFor = (currency: string): { id: string; label: string }[] =>
    all.filter((a) => canBeCounterAccount(a, currency)).map((a) => ({ id: a.id, label: a.name }));

  const toggleGroup = (kind: ContraKind): void => {
    setCollapsedKinds((prev) => {
      const next = new Set(prev);
      if (next.has(kind)) next.delete(kind);
      else next.add(kind);
      return next;
    });
  };

  /**
   * 一张往来卡。
   *
   * 🔴 余额用 `<Amount>`（端侧唯一金额渲染处）⇒ 正负配色、千分位、等宽字体全站一致；
   * **不要在卡里写 `¥{...}`**（`AC-Q4.6` 的"守铁律 8"就是靠这一处收口）。
   */
  const renderCard = (card: ContraCardModel): ReactElement | null => {
    const account = all.find((x) => x.id === card.id);
    if (account === undefined) return null;
    const confirming = confirmingId === account.id;
    return (
      <Card key={card.id} variant="outlined" data-testid={`contra-card-${card.id}`}>
        <CardContent sx={{ pb: '12px !important' }}>
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }} noWrap title={card.name}>
                {card.name}
              </Typography>
              {/* 🔴 副行**恒有内容**（至少含归属人）—— 归属人 NOT NULL，不是可选信息 */}
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                noWrap
                data-testid={`contra-subtitle-${card.id}`}
              >
                {card.subtitle}
              </Typography>
            </Box>
            {account.hidden && <Chip size="small" label="已收起" sx={{ flexShrink: 0 }} />}
          </Stack>

          <Box data-testid={`contra-balance-${card.id}`}>
            <Amount
              cents={card.balanceCents as never}
              currency={card.currency}
              sx={{ mt: 1, fontSize: '1.25rem', fontWeight: 700, lineHeight: 1.2 }}
            />
          </Box>

          {/* ── meta 行：进度 · 到期 · 类型（说明文字，可换行） ── */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.5}
            flexWrap="wrap"
            useFlexGap
            sx={{ mt: 1, minHeight: 24 }}
            data-testid={`contra-meta-${card.id}`}
          >
            {card.progressText !== null && (
              <Typography variant="caption" color="text.secondary" data-testid={`contra-progress-${card.id}`}>
                {card.progressText}
              </Typography>
            )}
            {card.dueText !== null && (
              <Typography variant="caption" color="text.secondary" data-testid={`contra-due-${card.id}`}>
                {card.dueText}
              </Typography>
            )}
            {/* 🔴 类型 chip 用**中性** outlined —— 原型给应付用了 `tag-alert`（朱红），
                与铁律 8「红色不表示负债状态」相冲（登记见 changelog，不擅自改设计稿） */}
            <Chip size="small" variant="outlined" label={card.kindLabel} />
          </Stack>

          {/*
            ── 动作行：**独立一行、右对齐、等间距**（批次 `94` 的修复点）──
            🔴 与 meta 行分开是关键：两行混排时 `ml:auto` 只管第一行，换行后就散架了。
          */}
          <Divider sx={{ mt: 1 }} />
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="flex-end"
            spacing={1}
            sx={{ mt: 1, flexWrap: 'wrap', rowGap: 0.5 }}
            data-testid={`contra-actions-${card.id}`}
          >
            {confirming ? (
              <>
                <Typography variant="caption" color="error" sx={{ mr: 'auto' }}>
                  删除后不可恢复
                </Typography>
                <Button
                  size="small"
                  color="error"
                  variant="contained"
                  disabled={busy}
                  onClick={() => {
                    setConfirmingId(null);
                    onDelete(account);
                  }}
                  data-testid={`account-delete-confirm-${account.id}`}
                >
                  确认删除
                </Button>
                <Button
                  size="small"
                  onClick={() => {
                    setConfirmingId(null);
                  }}
                  disabled={busy}
                >
                  取消
                </Button>
              </>
            ) : (
              <>
                {/* 结算（收回 / 还款 / 核销）—— 可选项由 `contra-settle` 单点给出（批 82） */}
                {settleActionsFor(card.kind).length > 0 && (
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => {
                      setSettlingId(account.id);
                    }}
                    disabled={busy}
                    data-testid={`contra-settle-${account.id}`}
                  >
                    结算
                  </Button>
                )}
                {/* 报销到账 —— 只对 `reimburse` 类开放（`DR-048` D7） */}
                {card.kind === ContraKind.Reimburse && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      setReimburseId(account.id);
                    }}
                    disabled={busy}
                    data-testid={`contra-reimburse-${account.id}`}
                  >
                    报销到账
                  </Button>
                )}
                {/* 还本期 —— 贷款专用，**有待还期次才画**（不画灰项） */}
                {card.kind === ContraKind.Loan &&
                  loanProgressOf(account.id) !== undefined &&
                  loanProgressOf(account.id)!.pendingPeriods > 0 && (
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => {
                        setRepayId(account.id);
                      }}
                      disabled={busy}
                      data-testid={`contra-repay-${account.id}`}
                    >
                      还本期
                    </Button>
                  )}
                <Button
                  size="small"
                  onClick={() => {
                    setTimelineId(account.id);
                  }}
                  data-testid={`contra-timeline-${account.id}`}
                >
                  明细
                </Button>
                <Button
                  size="small"
                  onClick={() => {
                    onEdit(account);
                  }}
                  disabled={busy}
                  data-testid={`account-edit-${account.id}`}
                >
                  编辑
                </Button>
                <Button
                  size="small"
                  color="error"
                  onClick={() => {
                    setConfirmingId(account.id);
                  }}
                  disabled={busy}
                  data-testid={`account-delete-${account.id}`}
                >
                  删除
                </Button>
              </>
            )}
          </Stack>
        </CardContent>
      </Card>
    );
  };

  return (
    <>
      <Card data-testid="contra-section" data-anchor="债权债务账户">
        <CardContent sx={{ pb: 1 }}>
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                债权债务账户（{contraTotal}）
              </Typography>
              <Typography variant="caption" color="text.secondary">
                一条关系 / 一个事项 = 一个账户 · 余额由腿派生 · 本金不计收支
              </Typography>
            </Box>
            <Button
              variant="contained"
              onClick={onCreate}
              disabled={busy}
              sx={{ flexShrink: 0 }}
              data-testid="account-new-contra"
            >
              新建
            </Button>
          </Stack>
        </CardContent>

        {contraTotal === 0 ? (
          <>
            <Divider />
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                还没有债权债务账户。借出 / 借入 / 垫付 / 报销可以各建一个账户 ——
                对手方与用途分两层记录，还款走结算而不进支出。
              </Typography>
            </CardContent>
          </>
        ) : (
          <CardContent sx={{ pt: 0 }}>
            <Stack spacing={2} divider={<Divider flexItem />}>
              {contraGroups.map((g) => {
                const collapsed = collapsedKinds.has(g.kind);
                return (
                  <Box key={g.kind} data-testid={`contra-group-${g.kind}`}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: collapsed ? 0 : 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {g.label}（{g.cards.length}）
                      </Typography>
                      <Button
                        size="small"
                        onClick={() => {
                          toggleGroup(g.kind);
                        }}
                        data-testid={`contra-toggle-${g.kind}`}
                        aria-expanded={!collapsed}
                      >
                        {collapsed ? '展开' : '收起'}
                      </Button>
                    </Stack>
                    {!collapsed && (
                      <Grid cols={3} sx={{ alignItems: 'start' }}>
                        {g.cards.map((c) => renderCard(c))}
                      </Grid>
                    )}
                  </Box>
                );
              })}
            </Stack>
          </CardContent>
        )}
      </Card>

      {/* ── 结算表单（内联，不弹 Dialog —— 与 `AccountForm` 同取向） ── */}
      {settlingAccount !== undefined && (
        <SettleForm
          account={settlingAccount}
          today={localToday()}
          counterOptions={counterOptionsFor(settlingAccount.currency)}
          busy={busy}
          onSettled={() => {
            setSettlingId(null);
            void qc.invalidateQueries({ queryKey: ['accounts'] });
            void qc.invalidateQueries({ queryKey: ['networth'] });
            void qc.invalidateQueries({ queryKey: ['contraTimeline'] });
          }}
          onCancel={() => {
            setSettlingId(null);
          }}
        />
      )}

      {/* ── L3 明细抽屉（`AC-Q4.3`） ── */}
      <ContraTimelineDrawer
        accountId={timelineId}
        onClose={() => {
          setTimelineId(null);
        }}
      />

      {/* ── 报销到账（预演 → 可微调 → 确认 · `DR-048`） ── */}
      {reimburseAccount !== null && (
        <ReimburseDialog
          open
          account={reimburseAccount}
          today={localToday()}
          counterOptions={counterOptionsFor(reimburseAccount.currency)}
          categoryNameOf={(categoryId) =>
            categories.data?.items.find((c) => c.id === categoryId)?.name ?? null
          }
          onClose={() => {
            setReimburseId(null);
          }}
          onDone={() => {
            setReimburseId(null);
            void qc.invalidateQueries({ queryKey: ['accounts'] });
            void qc.invalidateQueries({ queryKey: ['contraTimeline'] });
            void qc.invalidateQueries({ queryKey: ['transactions'] });
            void qc.invalidateQueries({ queryKey: ['networth'] });
          }}
        />
      )}

      {/* ── 还本期 / 提前还款（`DR-049`） ── */}
      {repayAccount !== null && (
        <LoanRepayDialog
          open
          account={repayAccount}
          today={localToday()}
          counterOptions={counterOptionsFor(repayAccount.currency)}
          onClose={() => {
            setRepayId(null);
          }}
          onDone={() => {
            setRepayId(null);
            // `DR-049` D7 的六处失效
            void qc.invalidateQueries({ queryKey: ['accounts'] });
            void qc.invalidateQueries({ queryKey: ['loanProgress'] });
            void qc.invalidateQueries({ queryKey: ['contraTimeline'] });
            void qc.invalidateQueries({ queryKey: ['transactions'] });
            void qc.invalidateQueries({ queryKey: ['networth'] });
            void qc.invalidateQueries({ queryKey: ['upcoming'] });
          }}
        />
      )}
    </>
  );
}
